/**
 * INSIGHT posting operator copy — ported verbatim from Desk
 * underwriting-workbench/apps/web/src/lib/work-items/posting-refusals.ts
 * (and TRANSACTION_POSTING_UNAVAILABLE_REASON for switched_off).
 *
 * Keep these strings exactly as written; they are load-bearing operator copy.
 */

/** Default named-contact placeholder when no specific person is configured. */
export const DEFAULT_POSTING_ESCALATION_CONTACT = "your posting administrator";

export const BUTTON_POST_TRANSACTION = "Post this transaction";

export const DISARMED_HELPER =
  "You can't post this yet. The checks below aren't all clear.";

export const OUTCOME_UNKNOWN =
  "We don't know yet whether this posted. Don't post again — check what INSIGHT recorded.";

export const STATUS_NONE =
  "Nothing has been drafted for this billing line yet.";

export const MODAL_YOUR_CONFIRMATION = "Your confirmation";
export const MODAL_WHO_WE_ARE_INVOICING = "Who we're invoicing";
export const MODAL_ACCOUNTING_MONTH = "Accounting month";

export const CHECK_WHAT_INSIGHT_RECORDED = "Check what INSIGHT recorded";

export const POSTING_REFUSAL_PREMIUM_MISSING =
  "We don't have the accepted premium for this policy, so we can't post it. If we posted now, we'd have no independent figure to check the invoice against afterwards. Go back to the quote and confirm the accepted quote is marked as accepted with its premium.";

export function postingRefusalFiguresMismatch(input) {
  const named =
    (input && input.namedPerson && String(input.namedPerson).trim()) ||
    DEFAULT_POSTING_ESCALATION_CONTACT;
  return (
    "The premium in the INSIGHT draft is $" +
    input.actual +
    ", but the client accepted $" +
    input.expected +
    " — a difference of $" +
    input.difference +
    ". Posting would invoice the client an amount they didn't agree to. Don't change anything in INSIGHT. Send this to " +
    named +
    " with both figures."
  );
}

export const POSTING_REFUSAL_WRONG_DEBTOR =
  'This invoice is currently addressed to the insured, not to the broker\'s account. If it posts like this, the invoice goes to the wrong party and the broker\'s account won\'t balance. Don\'t fix it in INSIGHT — press "Stop and escalate" so we correct the draft properly.';

export const POSTING_REFUSAL_CLIENT_CARD_DEBTOR =
  'This invoice would go to the insured, not the producing broker. On the Insight client card, set usual debtor and first authorised rep to the producing broker, then post from here. Do not change the draft. Do not post from Insight.';

export const POSTING_REFUSAL_SCHEDULE_NOT_ATTACHED =
  'The policy schedule isn\'t on the INSIGHT draft yet. We don\'t post a transaction without the document the client will be sent. Press "Produce the schedule" — it takes a few seconds — and this screen will unlock.';

export function postingRefusalBreakdownFailing(plainReason) {
  return (
    "The premium breakdown doesn't hang together: " +
    plainReason +
    ". Posting a transaction whose splits don't balance will show up as an error in the month-end accounts. Fix the figures at the source in the quote, then come back here."
  );
}

export function postingRefusalEndorsementOutOfOrder(earlierEndorsement) {
  return (
    "There's an earlier endorsement on this policy that hasn't been posted. Endorsements have to go into INSIGHT in the order they happened, or the premium history won't add up. Post " +
    earlierEndorsement +
    " first, then come back to this one."
  );
}

export function postingRefusalNotAllowlisted(namedPerson) {
  const named =
    (namedPerson && String(namedPerson).trim()) ||
    DEFAULT_POSTING_ESCALATION_CONTACT;
  return (
    "Posting from this app is switched on for a short list of policies while we prove it out, and this policy isn't on that list. This is deliberate, not a fault. If this policy should be posted from the app, ask " +
    named +
    " to add it. Otherwise, post it the usual way in INSIGHT."
  );
}

/** Identical wording to Desk TRANSACTION_POSTING_UNAVAILABLE_REASON. */
export const POSTING_REFUSAL_SWITCHED_OFF =
  "Posting from this app is currently switched off. Everything up to this point has been saved — the draft and the schedule are sitting in INSIGHT, unposted, and nothing has been lost. Come back when it's switched on, or post this draft manually in INSIGHT.";

/** Label for the orphan-period recovery token field on the posting rail. */
export const REFUSAL_RECOVERY_ACKNOWLEDGEMENT_LABEL = "Recovery acknowledgement";

/**
 * Operator copy when assemble/create-draft refuses with renewal_orphan_period
 * (dangling INSIGHT period with no latest transaction — Anstee period 61).
 */
export const POSTING_REFUSAL_RENEWAL_ORPHAN_PERIOD =
  "This renewal period has no latest transaction in INSIGHT (an orphan period). " +
  "Type the Recovery acknowledgement exactly as POL-11312:50:87:61:91 for this Anstee case " +
  "(or the token create-draft asks for), then press Create draft once. " +
  "Do not change anything in INSIGHT.";

export function postingRefusalSomeoneElsePosting(input) {
  return (
    "Someone else is posting this policy right now — " +
    input.name +
    ", started " +
    input.startedAt +
    ". Two people posting the same transaction is how a client gets invoiced twice. Leave it. Refresh in a minute and this card will show the result."
  );
}

export function postingRefusalAlreadyPosted(input) {
  return (
    "This policy has already been posted. Invoice " +
    input.invoiceNumber +
    " was raised on " +
    input.date +
    " for $" +
    input.amount +
    ". Posting again would raise a second invoice for the same cover. Nothing to do here — if the invoice is wrong, it has to be corrected with a reversing transaction in INSIGHT."
  );
}

/**
 * Map a server refusal code (+ optional fields) to operator-facing copy.
 * @param {string} code
 * @param {Object} [fields]
 * @returns {string}
 */
export function refusalMessage(code, fields) {
  const f = fields && typeof fields === "object" ? fields : {};
  switch (code) {
    case "premium_missing":
      return POSTING_REFUSAL_PREMIUM_MISSING;
    case "figures_mismatch": {
      // Pre-post proof failures reuse this code but do not always carry two dollar figures.
      // Prefer the server message over the "$? / $?" template when amounts are absent.
      if (f.actual == null || f.expected == null) {
        if (f.message) {
          return String(f.message);
        }
        return (
          "The live INSIGHT money proof does not match the reviewed draft. " +
          "Nothing was posted. Refresh and try again, or send this reason code to " +
          DEFAULT_POSTING_ESCALATION_CONTACT +
          "."
        );
      }
      const actual = String(f.actual);
      const expected = String(f.expected);
      let difference = f.difference != null ? String(f.difference) : "";
      if (!difference) {
        const delta = Math.abs(Number(f.actual) - Number(f.expected));
        difference = Number.isFinite(delta) ? delta.toFixed(2) : "?";
      }
      return postingRefusalFiguresMismatch({
        actual: actual,
        expected: expected,
        difference: difference || "?",
        namedPerson: f.namedPerson
      });
    }
    case "wrong_debtor":
      return POSTING_REFUSAL_WRONG_DEBTOR;
    case "client_card_debtor":
      return POSTING_REFUSAL_CLIENT_CARD_DEBTOR;
    case "schedule_not_attached":
      return POSTING_REFUSAL_SCHEDULE_NOT_ATTACHED;
    case "breakdown_failing":
      return postingRefusalBreakdownFailing(
        f.plainReason || f.reason || "the figures don't balance"
      );
    case "endorsement_out_of_order":
      return postingRefusalEndorsementOutOfOrder(
        f.earlierEndorsement || f.endorsement || "the earlier endorsement"
      );
    case "not_allowlisted":
      return postingRefusalNotAllowlisted(f.namedPerson);
    case "switched_off":
      return POSTING_REFUSAL_SWITCHED_OFF;
    case "someone_else_posting":
      return postingRefusalSomeoneElsePosting({
        name: f.name || "another underwriter",
        startedAt: f.startedAt || "just now"
      });
    case "already_posted":
      return postingRefusalAlreadyPosted({
        invoiceNumber: f.invoiceNumber || f.invoice || "—",
        date: f.date || "—",
        amount: f.amount != null ? String(f.amount) : "—"
      });
    case "no_draft":
      return STATUS_NONE;
    case "renewal_orphan_period":
      return POSTING_REFUSAL_RENEWAL_ORPHAN_PERIOD;
    case "confirmation_required":
      return "Confirm this post before continuing — tick " + MODAL_YOUR_CONFIRMATION + ".";
    case "policy_number_mismatch": {
      // Name what the draft holds. The operator types the number on screen, so a bare
      // "doesn't match" is unactionable when the draft was seeded with another identifier
      // for the same policy (Zoho record name POL-11312 vs carrier number FUSE-9754).
      const identities = Array.isArray(f.expectedIdentities)
        ? f.expectedIdentities.filter(Boolean)
        : f.expected
        ? [f.expected]
        : [];
      if (!identities.length) {
        return "The policy number you typed doesn't match this policy. Type it exactly, then try again.";
      }
      return (
        "The policy number you typed doesn't match the draft this screen is posting. The draft was created for " +
        identities.map(value => '"' + value + '"').join(" or ") +
        (f.transactionId ? " (INSIGHT transaction " + f.transactionId + ")" : "") +
        ". If that is the right policy, type it exactly as shown here. If it is not, the draft was built against the wrong policy — do not post it; re-create the draft."
      );
    }
    case "accounting_month_required":
      return "Choose an " + MODAL_ACCOUNTING_MONTH.toLowerCase() + " before posting.";
    case "debtor_missing":
      return POSTING_REFUSAL_WRONG_DEBTOR;
    default:
      return (
        "We couldn't post this transaction. " +
        (f.message || "Check the details on this screen, or ask " + DEFAULT_POSTING_ESCALATION_CONTACT + ".")
      );
  }
}

export default {
  BUTTON_POST_TRANSACTION,
  DISARMED_HELPER,
  OUTCOME_UNKNOWN,
  STATUS_NONE,
  MODAL_YOUR_CONFIRMATION,
  MODAL_WHO_WE_ARE_INVOICING,
  MODAL_ACCOUNTING_MONTH,
  CHECK_WHAT_INSIGHT_RECORDED,
  DEFAULT_POSTING_ESCALATION_CONTACT,
  POSTING_REFUSAL_PREMIUM_MISSING,
  POSTING_REFUSAL_WRONG_DEBTOR,
  POSTING_REFUSAL_CLIENT_CARD_DEBTOR,
  POSTING_REFUSAL_SCHEDULE_NOT_ATTACHED,
  POSTING_REFUSAL_SWITCHED_OFF,
  REFUSAL_RECOVERY_ACKNOWLEDGEMENT_LABEL,
  POSTING_REFUSAL_RENEWAL_ORPHAN_PERIOD,
  postingRefusalFiguresMismatch,
  postingRefusalBreakdownFailing,
  postingRefusalEndorsementOutOfOrder,
  postingRefusalNotAllowlisted,
  postingRefusalSomeoneElsePosting,
  postingRefusalAlreadyPosted,
  refusalMessage
};
